import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useDmCompliance } from '../../../../hooks/useDmCompliance';
import { useElementWidth } from '../../../../hooks/useElementWidth';
import type { PatientMonitoringBlock } from '../../../../services/patientCard';
import { PaceRing } from '../PaceRing';
import { ProgressBar } from '../ProgressBar';
import { ScanComplianceRing } from '../ScanComplianceRing';
import { STAT_RING_MAX_SIZE, statRingSizeForRow } from '../statRingSize';
import {
    StatRingSizeProvider,
    statRingSlotClass,
    useStatRingFitSize
} from '../StatRingSizeContext';
import { Card } from '../shared';

const WRAPPING_ROW_CLASS = 'flex flex-1 flex-wrap gap-4 items-center content-center justify-around';
// Rings align on top so labels wrapping onto a second line don't push them out of line.
const FITTED_ROW_CLASS = 'flex flex-1 gap-2 items-start justify-around';
const FITTED_ROW_GAP_PX = 8; // gap-2

interface TreatmentCardProps {
    patientId: string;
    monitoring: PatientMonitoringBlock | undefined;
    /** Hide the "Treatment Progress" header (e.g. in space-constrained surfaces). */
    hideTitle?: boolean;
    /** Keep every ring on one row, shrinking them with the card, for narrow side columns. */
    fitRow?: boolean;
}

function RingSkeleton() {
    const fitSize = useStatRingFitSize();
    const size = fitSize ?? STAT_RING_MAX_SIZE;
    return (
        <div className={statRingSlotClass(fitSize != null)}>
            <div
                className="rounded-full bg-gray-100 animate-pulse"
                style={{ width: size, height: size }}
            />
            <div className="h-3 w-20 max-w-full bg-gray-100 animate-pulse rounded" />
            <div className="h-3 w-24 max-w-full bg-gray-100 animate-pulse rounded" />
        </div>
    );
}

function TreatmentCard({ patientId, monitoring, hideTitle, fitRow = false }: TreatmentCardProps) {
    const dmId = monitoring?.dmId ?? null;
    const { data: compliance, isLoading: complianceLoading } = useDmCompliance(patientId, dmId);
    const { ref: rowRef, width: rowWidth } = useElementWidth<HTMLDivElement>();
    const ringCount = dmId != null ? 3 : 1;
    const fitSize = fitRow ? statRingSizeForRow(rowWidth, ringCount, FITTED_ROW_GAP_PX) : null;

    return (
        <Card title={hideTitle ? undefined : 'Treatment Progress'}>
            <div
                ref={fitRow ? rowRef : undefined}
                className={fitRow ? FITTED_ROW_CLASS : WRAPPING_ROW_CLASS}
            >
                <StatRingSizeProvider value={fitSize}>
                    <ProgressBar
                        current={monitoring?.currentAligner ?? null}
                        total={monitoring?.totalAligners ?? null}
                    />

                    {dmId != null &&
                        (complianceLoading ? (
                            <>
                                <RingSkeleton />
                                <RingSkeleton />
                            </>
                        ) : (
                            <>
                                <PaceRing
                                    pacePct={compliance?.trackingPct ?? null}
                                    goCount={compliance?.goCount ?? 0}
                                    nogoCount={compliance?.nogoCount ?? 0}
                                    goBackCount={compliance?.goBackCount ?? 0}
                                    tracksAligners={compliance?.tracksAligners ?? true}
                                />
                                <ScanComplianceRing
                                    lateCount={compliance?.lateCount ?? 0}
                                    onTimeCount={compliance?.onTimeCount ?? 0}
                                />
                            </>
                        ))}
                </StatRingSizeProvider>
            </div>

            {dmId != null && compliance && compliance.consecutiveNogos > 0 && (
                <div className="flex items-center gap-1.5 mt-4 px-3 py-2 rounded-lg bg-amber-50 text-amber-700">
                    <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
                    <span className="text-xs font-semibold">
                        {compliance.consecutiveNogos} consecutive NO-GO
                        {compliance.consecutiveNogos > 1 ? 's' : ''}
                    </span>
                </div>
            )}
        </Card>
    );
}

export { TreatmentCard };
